import * as React from 'react';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';

import './styles.css';

enum SortDirection {
    Ascending,
    Descending
}

interface SortableState {
    sortDirection: SortDirection;
    sortableColIndex: number;
}

interface TableState {
    pageIndex: number;
    showJumpToPageInput: boolean;
    selectedIds: { [id: string]: string };
    sortableState: SortableState;
    jumpToPageInputValue: string;
}

export interface TableColumn<TRowModel> {
    value: string | JSX.Element | null;
    isSortable?: boolean;
    getSortableValue?: (model: TRowModel) => string;
}

export interface TableRow {
    cols: (string | JSX.Element | { content: JSX.Element })[];
}

export interface SelectingRowOptions<TRowModel> {
    rowModelGetId: (model: TRowModel) => string;
    onRowSelected: (selectedIds: { [id: string]: string }) => void;
    initiallySelected?: { [id: string]: string };
    showOnlyOnHover?: boolean; // default value false
    singleSelect?: boolean;
}

export interface ToxnotReactTableProps<TRowModel> {
    headerRow: TableColumn<TRowModel>[];
    allRowModels: TRowModel[];
    getRowForTable: (data: TRowModel, rowIndex: number) => TableRow;
    rowsPerPage?: number;
    rowClickEventHandler?: (e: React.MouseEvent, rowModel: TRowModel) => void;
    fullSectionWidth?: boolean;
    localizer: IStringLocalizer;
    stickyHeader?: boolean;
    enablePaging?: boolean; // User needs to set this to true to enable paging controls for both server and client-side.
    selectingRowOptions?: SelectingRowOptions<TRowModel>;
    defaultSortColumn?: number;
    onPageChange?: (pageNumber: number) => void; // We are handling server-side paging based on onPageChange. This will be called when the next/prev button is clicked.
    onSortChange?: (sortableColIndex: number, sortAscending: boolean) => void;
    totalNoResult?: number;
    currentPage?: number;
    noResultFoundContent?: string | JSX.Element;
    selectAllAcrossAllPages?: boolean; // If true, select all checkbox selects all items across all pages, not just current page
    disableClientSorting?: boolean;
    usePublicationsSortBehavior?: boolean;
}

export class ToxnotReactTable<TRowModel> extends React.PureComponent<ToxnotReactTableProps<TRowModel>, TableState> {
    // ===============================================================================

    private _sortDirectionToGlyphicon = {
        [SortDirection.Ascending]: "glyphicon-chevron-down",
        [SortDirection.Descending]: "glyphicon-chevron-up"
    }

    // ===============================================================================

    constructor(props: ToxnotReactTableProps<TRowModel>) {
        super(props);

        // set state
        this.state = {
            pageIndex: this.props.currentPage ?? 0,
            showJumpToPageInput: false,
            selectedIds: this.props.selectingRowOptions?.initiallySelected ?? {},
            sortableState: {
                sortableColIndex: props.defaultSortColumn ?? -1,
                sortDirection: SortDirection.Ascending
            },
            jumpToPageInputValue: ''
        };
    }

    public componentDidUpdate(prevProps: ToxnotReactTableProps<TRowModel>) {
        if (prevProps.currentPage !== this.props.currentPage) {
            this.setState({ pageIndex: this.props.currentPage ?? 0 });
        }
    }

    // ===============================================================================

    public render() {
        const rows: JSX.Element[] = [];

        const noResultFoundContent = this.props.noResultFoundContent ?? this.props.localizer.localizeString("No Items Found");

        const rowModelsToRender = this.getModelsForCurrentPage();

        for (let i = 0; i < rowModelsToRender.length; ++i) {
            rows.push(this.renderTableRow(rowModelsToRender[i], i));
        }

        const useFullWitdth = this.props.fullSectionWidth ?? false;

        return (
            <div className={`tn-react-table-container${useFullWitdth ? " full-section-width" : ""}`}>
                {this.shouldShowPagingAction() && this.renderPagingActions(true)}
                <table className="table toxnot-table-v2 tn-react-table">
                    <thead>
                        {this.renderTableHeaderRow()}
                    </thead>
                    <tbody>
                        {rows}
                    </tbody>
                </table>
                {rows.length === 0 && <div className="empty-message-container">{typeof noResultFoundContent === 'string' ? noResultFoundContent : noResultFoundContent}</div>}
                {this.shouldShowPagingAction() && this.renderPagingActions(false)}
            </div>
        )
    }

    // ===============================================================================

    private renderTableHeaderRow() {
        const rowCols: JSX.Element[] = [];

        let colIndex = 0;

        if (this.getSelectRowsEnabled()) {
            rowCols.push(this.renderCheckboxColumn(colIndex, undefined, true))
            ++colIndex;
        }

        for (let col of this.props.headerRow) {
            rowCols.push(this.renderTableHeaderColumn(col, colIndex));
            ++colIndex;
        }

        return (
            <tr className={this.props.selectingRowOptions?.showOnlyOnHover ? "checkbox-on-hover" : ""}>
                {rowCols}
            </tr>
        );
    }

    // ===============================================================================

    private renderTableRow(model: TRowModel, rowIndex: number) {
        const reactRow = this.props.getRowForTable(model, rowIndex);
        const rowCols: JSX.Element[] = [];

        let colIndex = 0;

        if (this.getSelectRowsEnabled()) {
            rowCols.push(this.renderCheckboxColumn(colIndex, model, false))
            ++colIndex;
        }

        for (let col of reactRow.cols) {
            rowCols.push(this.renderTableColumn(col, colIndex));
            ++colIndex;
        }

        return (
            <tr
                key={rowIndex}
                onClick={(e) => this.row_click(e, model)}
                className={`${this.props.rowClickEventHandler ? "row-with-callback" : ""} ${this.props.selectingRowOptions?.showOnlyOnHover ? " checkbox-on-hover" : ""
                    }`.trim()}
            >
                {rowCols}
            </tr>
        );
    }

    // ===============================================================================

    private getCorrectColumnIndex(index: number) {
        return this.props.selectingRowOptions ? index - 1 : index;
    }

    // ===============================================================================

    private renderTableHeaderColumn(tableCol: TableColumn<TRowModel>, index: number) {
        let body = tableCol.value;

        if (tableCol.isSortable) {
            const correctIndex = this.getCorrectColumnIndex(index)
            body = (
                <div>
                    <a
                        role="button"
                        className="column-sort-button"
                        onClick={(e) => this.col_click(correctIndex)}
                    > {tableCol.value} </a>
                    {" "}
                    {this.state.sortableState.sortableColIndex === correctIndex && (
                        <span className={`glyphicon ${this._sortDirectionToGlyphicon[this.state.sortableState.sortDirection]}`}></span>
                    )}
                </div>
            )
        }

        return (
            <td key={index} className={this.getStickyHeader() ? "sticky-header" : ""}>
                {body}
            </td>
        );
    }


    // ===============================================================================

    private renderTableColumn(value: string | null | JSX.Element | { content: JSX.Element }, index: number) {
        // Check if value is an object with a content property. If so, return the content directly.
        // Note: To use colspan, pass an object with a content property that contains a <td> with the colSpan attribute.
        if (typeof value === 'object' && value !== null && 'content' in value) {
            return value.content;
        }

        // rendering logic for normal columns
        return (
            <td key={index}>
                {value}
            </td>
        );
    }

    // ===============================================================================

    private renderCheckboxColumn = (index: number, rowModel: TRowModel | undefined, isHeaderCheckbox: boolean) => {
        let retElement: JSX.Element;

        if (isHeaderCheckbox) {
            retElement = (
                <input
                    type="checkbox"
                    disabled={this.props.selectingRowOptions?.singleSelect}
                    onChange={this.selectAllRows_onChange}
                    checked={this.isWholePageChecked()}
                />
            )
        } 
        else {
            if (!rowModel) {
                throw new Error("Unable to render table row. Insufficient data given for row.");
            }
        
            retElement = (
                <input
                    type="checkbox"
                    onClick={(e) => this.selectRow_click(e, rowModel)}
                    onChange={(e) => { }}
                    checked={this.state.selectedIds[this.getSelectingRowOptions().rowModelGetId(rowModel)] !== undefined}
                />
            )
        }
        
        const stickyHeader = this.props.stickyHeader ?? true;

        return (
            <td key={index} className={`selector checkbox-column ${isHeaderCheckbox && this.getStickyHeader() ? "sticky-header" : ""}`}>
                {retElement}
            </td>
        );
    }

    // ===============================================================================

    private isWholePageChecked(): boolean {
        const models = this.props.selectAllAcrossAllPages ?
            this.props.allRowModels :
            this.getModelsForCurrentPage();

        if (models.length === 0) {
            return false;
        }

        for (let model of models) {
            if (!this.state.selectedIds[this.getSelectingRowOptions().rowModelGetId(model)]) {
                return false;
            }
        }

        return true;
    }

    // ===============================================================================

    private renderPagingActions(showJumpToPage: boolean) {
        return (
            <div className="paging-controls-container react-paging-controls-container form-inline">
                <span className="paging-info">
                    {this.props.localizer.localizeStringFormat("Page ") + (this.state.pageIndex + 1).toString() + this.props.localizer.localizeStringFormat(" of ") + this.getTotalNumPages().toString()}
                </span>{" "}
                {this.state.pageIndex > 0 && (
                    <button
                        type="button"
                        className="btn btn-default btn-sm prev-page-button pagination-immunity-control"
                        onClick={(e) => this.prevPage_click()}
                        disabled={false}
                    >
                        <span className="glyphicon glyphicon-chevron-left"></span>
                    </button>
                )}
                {" "}
                {this.state.pageIndex + 1 < this.getTotalNumPages() && (
                    <button
                        type="button"
                        className="btn btn-default btn-sm next-page-button pagination-immunity-control"
                        onClick={(e) => this.nextPage_click()}
                        disabled={false}
                    >
                        <span className="glyphicon glyphicon-chevron-right"></span>
                    </button>
                )}
                {" "}
                {showJumpToPage &&
                    <>
                        <a
                            role="button"
                            className="jump-to-page-link pagination-immunity-control"
                            onClick={(e) => this.jumpToPage_click()}
                            tabIndex={0}
                        >
                            {this.props.localizer.localizeString("Jump to Page")}
                        </a>{" "}
                        {this.state.showJumpToPageInput && (
                            <input
                                type="text"
                                role="textbox"
                                onKeyDown={this.jumpToPage_keyDown}
                                onChange={this.jumpToPageInput_onChange}
                                className="form-control jump-to-page-input pagination-immunity-control"
                                disabled={false}
                                placeholder="#"
                                value={this.state.jumpToPageInputValue}
                            />
                        )}
                    </>
                }
            </div>
        );
    }

    // ===============================================================================

    private getModelsForCurrentPage() {
        const models = this.sortModels(this.props.allRowModels);
        
        /*
            this.getEnablePaging is used to show paging controls for both server and client-side.
            We are handling server-side paging based on onPageChange.
            It will be called when the next/prev button is clicked.
        */
        if (this.getEnablePaging() && !this.props.onPageChange) {

            const rowsPerPage = this.getRowsPerPage();
            
            const startIndex = this.state.pageIndex * rowsPerPage;
            
            const endIndex = Math.min(models.length - 1, startIndex + rowsPerPage - 1);
            
            return models.slice(startIndex, endIndex + 1);
        }
        else {
            return models;
        }
    }

    // ===============================================================================

    private getRowsPerPage() {
        return this.props.rowsPerPage ?? 50;
    }

    // ===============================================================================

    private getTotalNumPages() {
        const rowCount = this.props.totalNoResult ? this.props.totalNoResult : this.props.allRowModels.length;
        const rowsPerPage = this.getRowsPerPage();

        // Calculate the total number of pages without enforcing a minimum of 1
        const totalNumPages = Math.ceil(rowCount / rowsPerPage);
        
        return totalNumPages;
    }

    // ===============================================================================

    private getEnablePaging() {
        return this.props.enablePaging ?? true;
    }

    // ===============================================================================

    private shouldShowPagingAction() {
        return this.getTotalNumPages() > 1 && this.getEnablePaging()
    }

    // ===============================================================================

    private getSelectRowsEnabled() {
        return this.props.selectingRowOptions !== undefined;
    }

    // ===============================================================================

    private getSelectingRowOptions() {
        if (!this.props.selectingRowOptions) {
            throw new Error("Selecting Row Options must be set to true.")
        }

        return this.props.selectingRowOptions;
    }

    // ===============================================================================

    private getStickyHeader() {
        return this.props.stickyHeader ?? true;
    }

    // ===============================================================================

    private sortModels(models: TRowModel[]) {
        if (this.props.disableClientSorting) {
            return models;
        }

        const sortableColIndex = this.state.sortableState.sortableColIndex;

        // if no sort column set or column set from defaultSortColumn value is invalid return models
        if (sortableColIndex === -1 || this.props.headerRow.length < sortableColIndex) {
            return models;
        }

        const getSortableValueFn = this.props.headerRow[sortableColIndex].getSortableValue;

        if (!getSortableValueFn) {
            return models;
        }

        return [...models].sort(
            (a: TRowModel, b: TRowModel) => {
                const s_a = getSortableValueFn(a);
                const s_b = getSortableValueFn(b);

                const i = s_a.localeCompare(s_b);

                return this.state.sortableState.sortDirection === SortDirection.Ascending ? i : i * -1;
            });
    }

    // ===============================================================================

    private getOppositeSortDirection(sortDirection: SortDirection) {
        return sortDirection === SortDirection.Ascending ? SortDirection.Descending : SortDirection.Ascending;
    }

    // ===============================================================================

    //
    // UI Events
    //

    // ===============================================================================

    private row_click = (e: React.MouseEvent, model: TRowModel) => {
        $(e.currentTarget).parent().find("tr").removeClass("selected");
        $(e.currentTarget).addClass("selected");

        if (!this.props.rowClickEventHandler) {
            return;
        }

        this.props.rowClickEventHandler(e, model);
    }

    // ===============================================================================

    private col_click = (index: number) => {
        const { sortableColIndex, sortDirection } = this.state.sortableState;
        const nextSortDirection = this.props.usePublicationsSortBehavior && sortableColIndex !== index
            ? SortDirection.Ascending
            : this.getOppositeSortDirection(sortDirection);

        this.setState({
            sortableState: {
                sortDirection: nextSortDirection,
                sortableColIndex: index
            }
        }, () => {
            if (this.props.onSortChange) {
                this.props.onSortChange(index, nextSortDirection === SortDirection.Ascending);
            }
        });
    }

    // ===============================================================================

    private nextPage_click = () => {
        const nextPageIndex = this.state.pageIndex + 1;

        if (this.props.onPageChange) {
            this.props.onPageChange(nextPageIndex);
        } else {
            if (nextPageIndex > this.getTotalNumPages()) {
                return;
            }
    
            this.setState({ pageIndex: nextPageIndex });
        }
    }

    // ===============================================================================

    private prevPage_click = () => {
        const prevPageIndex = this.state.pageIndex - 1;

        if (prevPageIndex >= 0) {
            if (this.props.onPageChange) {
                this.props.onPageChange(prevPageIndex);
            } else {
                this.setState({ pageIndex: prevPageIndex });
            }
        }
    }

    // ===============================================================================

    private jumpToPage_click = () => {
        this.setState({
            pageIndex: this.state.pageIndex,
            showJumpToPageInput: !this.state.showJumpToPageInput
        });
    }

    // ===============================================================================

    private jumpToPage_keyDown = (e: React.KeyboardEvent) => {
        if (!/^\d$/.test(e.key) && e.key !== "Backspace" && e.key !== "Enter") {
            e.preventDefault();
            return;
        }

        if (e.key === "Enter") {
            const possibleNumberStr = this.state.jumpToPageInputValue;
            
            if (!possibleNumberStr) {
                return;
            }
    
            const possibleNumber = parseInt(possibleNumberStr, 10);
    
            if (Number.isNaN(possibleNumber)) {
                return;
            }
    
            const pageIndexToJumpTo = possibleNumber - 1;
    
            if (pageIndexToJumpTo >= this.getTotalNumPages() || pageIndexToJumpTo < 0) {
                return;
            }
    
            this.setState({ 
                pageIndex: pageIndexToJumpTo,
                jumpToPageInputValue: ''
            }, () => {
                if (this.props.onPageChange) {
                    this.props.onPageChange(pageIndexToJumpTo);
                }
            });
        }
    }

    // ===============================================================================

    private jumpToPageInput_onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            jumpToPageInputValue: e.target.value
        });
    }

    // ===============================================================================

    private selectRow_click = (e: React.MouseEvent<HTMLInputElement>, rowModel: TRowModel) => {
        const currentSelectedIds = { ...this.state.selectedIds };
        const selectingRowOptions = this.getSelectingRowOptions();

        const id: string = selectingRowOptions.rowModelGetId(rowModel);

        if (e.currentTarget.checked) {
            currentSelectedIds[id] = id;
        }

        else {
            delete currentSelectedIds[id];
        }

        selectingRowOptions.onRowSelected(currentSelectedIds);

        this.setState({
            ...this.state,
            selectedIds: currentSelectedIds
        });

        e.stopPropagation();
    }

    // ===============================================================================

    private selectAllRows_onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const currentSelectedIds = { ...this.state.selectedIds };
        const selectingRowOptions = this.getSelectingRowOptions();

        let modelsToProcess;
        
        if (this.props.selectAllAcrossAllPages) {
            modelsToProcess = this.props.allRowModels;
        } else {
            modelsToProcess = this.getModelsForCurrentPage();
        }

        if (e.target.checked) {
            for (let model of modelsToProcess) {
                const id = selectingRowOptions.rowModelGetId(model);
                currentSelectedIds[id] = id;
            }
        }

        else {
            for (let model of modelsToProcess) {
                const id = selectingRowOptions.rowModelGetId(model);
                delete currentSelectedIds[id];
            }
        }

        selectingRowOptions.onRowSelected(currentSelectedIds);

        this.setState({
            ...this.state,
            selectedIds: currentSelectedIds
        });
    }
}
